import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useTheme } from '../design/ThemeProvider';
import { DAISY_THEMES, DEFAULT_THEME } from '../design/daisyThemes';

type AdminPanelProps = {
  isAdmin: boolean;
  onNavigateHome: () => void;
};

type AdminStats = {
  users: number;
  series: number;
  creators: number;
  listeners: number;
};

type AppThemeRow = {
  theme_name: string;
  is_enabled: boolean;
  is_default: boolean;
};

export function AdminPanel({ isAdmin, onNavigateHome }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<'stats' | 'themes'>(() => {
    if (typeof window === 'undefined') return 'stats';
    const stored = window.sessionStorage.getItem('admin-panel-tab');
    return stored === 'themes' ? 'themes' : 'stats';
  });
  const [stats, setStats] = useState<AdminStats>({
    users: 0,
    series: 0,
    creators: 0,
    listeners: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const { refreshThemes } = useTheme();
  const [themeDraft, setThemeDraft] = useState<AppThemeRow[]>([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [themesError, setThemesError] = useState<string | null>(null);
  const [themesSaving, setThemesSaving] = useState(false);
  const [themesSaveError, setThemesSaveError] = useState<string | null>(null);
  const [themesSaveStatus, setThemesSaveStatus] = useState<string | null>(null);
  const [defaultTheme, setDefaultTheme] = useState(DEFAULT_THEME);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('admin-panel-tab', activeTab);
  }, [activeTab]);

  const resolveDefaultTheme = (draft: AppThemeRow[], desired: string) => {
    const desiredRow = draft.find((theme) => theme.theme_name === desired && theme.is_enabled);
    if (desiredRow) return desired;
    const firstEnabled = draft.find((theme) => theme.is_enabled);
    return firstEnabled?.theme_name ?? DEFAULT_THEME;
  };

  const buildThemeDraft = (rows: AppThemeRow[]) => {
    const rowMap = new Map(rows.map((row) => [row.theme_name, row]));
    const hasRows = rows.length > 0;
    const draft = DAISY_THEMES.map((themeName) => {
      const row = rowMap.get(themeName);
      const isEnabled = row ? row.is_enabled : hasRows ? false : ['light', 'dark'].includes(themeName);
      return {
        theme_name: themeName,
        is_enabled: isEnabled,
        is_default: row?.is_default ?? false,
      };
    });
    const desiredDefault = rows.find((row) => row.is_default)?.theme_name ?? DEFAULT_THEME;
    return {
      draft,
      defaultTheme: resolveDefaultTheme(draft, desiredDefault),
    };
  };

  const handleToggleTheme = (themeName: string) => {
    setThemeDraft((prev) => {
      const next = prev.map((theme) =>
        theme.theme_name === themeName ? { ...theme, is_enabled: !theme.is_enabled } : theme,
      );
      const nextDefault = resolveDefaultTheme(next, defaultTheme);
      if (nextDefault !== defaultTheme) {
        setDefaultTheme(nextDefault);
      }
      return next;
    });
    setThemesSaveError(null);
    setThemesSaveStatus(null);
  };

  const handleDefaultTheme = (themeName: string) => {
    const isEnabled = themeDraft.find((theme) => theme.theme_name === themeName)?.is_enabled;
    if (!isEnabled) return;
    setDefaultTheme(themeName);
    setThemesSaveError(null);
    setThemesSaveStatus(null);
  };

  const handleSaveThemes = async () => {
    const enabled = themeDraft.filter((theme) => theme.is_enabled);
    if (enabled.length === 0) {
      setThemesSaveError('Enable at least one theme.');
      return;
    }
    if (!enabled.find((theme) => theme.theme_name === defaultTheme)) {
      setThemesSaveError('Default theme must be enabled.');
      return;
    }
    setThemesSaving(true);
    setThemesSaveError(null);
    const payload = themeDraft.map((theme) => ({
      theme_name: theme.theme_name,
      is_enabled: theme.is_enabled,
      is_default: theme.theme_name === defaultTheme,
      updated_at: new Date().toISOString(),
    }));
    const { error: saveError } = await supabase.from('app_themes').upsert(payload, {
      onConflict: 'theme_name',
    });
    setThemesSaving(false);
    if (saveError) {
      setThemesSaveError(saveError.message ?? 'Unable to save themes.');
      return;
    }
    setThemesSaveStatus('Themes updated.');
    refreshThemes();
  };

  useEffect(() => {
    if (!isAdmin) return;
    let isMounted = true;
    const loadThemes = async () => {
      setThemesLoading(true);
      setThemesError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('app_themes')
          .select('theme_name, is_enabled, is_default');
        if (fetchError) throw fetchError;
        if (!isMounted) return;
        const { draft, defaultTheme } = buildThemeDraft(data ?? []);
        setThemeDraft(draft);
        setDefaultTheme(defaultTheme);
      } catch (err: any) {
        if (isMounted) {
          setThemesError(err?.message ?? 'Unable to load themes.');
        }
      } finally {
        if (isMounted) {
          setThemesLoading(false);
        }
      }
    };

    loadThemes();
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let isMounted = true;
    const loadStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const [users, series, creators, listeners] = await Promise.all([
          supabase.from('profiles').select('id', { count: 'exact', head: true }),
          supabase.from('series').select('id', { count: 'exact', head: true }),
          supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'creator'),
          supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'listener'),
        ]);

        const firstError = users.error || series.error || creators.error || listeners.error;
        if (firstError) {
          throw firstError;
        }

        if (isMounted) {
          setStats({
            users: users.count ?? 0,
            series: series.count ?? 0,
            creators: creators.count ?? 0,
            listeners: listeners.count ?? 0,
          });
        }
      } catch (err: any) {
        if (isMounted) {
          setStatsError(err?.message ?? 'Unable to load admin stats.');
        }
      } finally {
        if (isMounted) {
          setStatsLoading(false);
        }
      }
    };

    loadStats();
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="page-shell flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">Not authorized</h1>
          <p className="text-sm text-base-content/60">You do not have access to the admin panel.</p>
          <button onClick={onNavigateHome} className="btn btn-ghost btn-primary btn-sm">
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <h1 className="text-3xl font-semibold">Admin Panel</h1>
        <p className="text-sm text-base-content/60">Manage platform statistics and themes.</p>
      </div>

      <div role="tablist" className="tabs tabs-bordered">
        <button
          role="tab"
          type="button"
          className={`tab ${activeTab === 'stats' ? 'tab-active' : ''}`}
          aria-selected={activeTab === 'stats'}
          onClick={() => setActiveTab('stats')}
        >
          Main stats
        </button>
        <button
          role="tab"
          type="button"
          className={`tab ${activeTab === 'themes' ? 'tab-active' : ''}`}
          aria-selected={activeTab === 'themes'}
          onClick={() => setActiveTab('themes')}
        >
          Themes
        </button>
      </div>

      {activeTab === 'stats' ? (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Main stats</h2>
            <span className="text-xs text-base-content/50">Live counts</span>
          </div>

          {statsError && (
            <div className="alert alert-error text-sm">
              <span>{statsError}</span>
            </div>
          )}

          {statsLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="skeleton h-24 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: 'Users total', value: stats.users },
                { label: 'Series total', value: stats.series },
                { label: 'Creators total', value: stats.creators },
                { label: 'Listeners total', value: stats.listeners },
              ].map((item) => (
                <div
                  key={item.label}
                  className="card border border-base-200 bg-base-100 shadow-sm"
                >
                  <div className="card-body p-5">
                    <p className="text-xs uppercase tracking-wide text-base-content/60">{item.label}</p>
                    <p className="text-2xl font-semibold text-base-content">
                      {item.value.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">Themes</h2>
              <p className="text-sm text-base-content/60">Enable themes and choose the default experience.</p>
            </div>
            <button
              type="button"
              onClick={handleSaveThemes}
              className="btn btn-primary btn-sm"
              disabled={themesSaving}
            >
              {themesSaving ? 'Saving...' : 'Save themes'}
            </button>
          </div>

          {themesError && (
            <div className="alert alert-error text-sm">
              <span>{themesError}</span>
            </div>
          )}

          {themesSaveError && (
            <div className="alert alert-warning text-sm">
              <span>{themesSaveError}</span>
            </div>
          )}

          {themesSaveStatus && (
            <div className="alert alert-success text-sm">
              <span>{themesSaveStatus}</span>
            </div>
          )}

          {themesLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="skeleton h-10 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-base-200 bg-base-100">
              <table className="table">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-base-content/60">
                    <th>Theme</th>
                    <th>Enabled</th>
                    <th>Default</th>
                  </tr>
                </thead>
                <tbody>
                  {themeDraft.map((theme) => (
                    <tr key={theme.theme_name}>
                      <td className="font-medium capitalize">{theme.theme_name}</td>
                      <td>
                        <input
                          type="checkbox"
                          className="toggle toggle-sm"
                          checked={theme.is_enabled}
                          onChange={() => handleToggleTheme(theme.theme_name)}
                        />
                      </td>
                      <td>
                        <input
                          type="radio"
                          name="default-theme"
                          className="radio radio-sm radio-primary"
                          checked={defaultTheme === theme.theme_name}
                          onChange={() => handleDefaultTheme(theme.theme_name)}
                          disabled={!theme.is_enabled}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
